/* eslint-disable react-hooks/exhaustive-deps */
import { SuccessfulAddMachineDialog } from '../components/machines/successfulAddMachineDialog';
import { AddMachineDialoge } from '../components/machines/addMachineDialoge';
import MachinePageHeader from '../components/machines/pageHeader';
import MachineFiltration from '../components/machines/filtration';
import { useState, useCallback, useEffect } from 'react';
import { useHasPermission } from '../hooks/useHasPermission';
import { Divider, LinearProgress, Box } from '@mui/material';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import { useLocation, useNavigate } from 'react-router-dom';
import GridView from '../components/machines/GridView';
import ListView from '../components/machines/ListView';
import { isObjectEmpty } from '../utils/isObjectEmpty';
import {
    ListNavigationProvider,
    useListPageNavigation,
    usePersistedListParams,
} from '../hooks/listNavigation';
import NoRecord from '../components/NoRecord';
import Page from '../components/Page';
import axios from 'axios';

const Machines = () => {

    const location = useLocation();
    const navigate = useNavigate();
    const hasMachineCreatePermission = useHasPermission('machine_create');

    const [loading, setLoading] = useState(true);
    const [machineId, setMachineId] = useState(null);
    const [openAddMachine, setOpenAddMachine] = useState(false);
    const [openSuccessfulAddMachine, setOpenSuccessfulAddMachine] = useState(false);

    const defaultSortBy = {
        name: null,
        region: null,
        site: null,
        pole: null,
        metier: null,
        marque: null,
        status: null,
        type: null,
        internal_codification: null,
        serial_number: null,
        control_date: null,
        next_control_date: null,
        contract_start_date: null,
        contract_end_date: null,
        purchase_date: null,
        purchase_price: null,
        commissioning_date: null,
        manufacture_date: null,
        electric_power: null,
        intensity: null,
        model: null,
        cost_status: null,
    };

    const {
        filters,
        setFilters,
        sortBy,
        setSortBy,
        displayMode: selectedDisplayMode,
        setDisplayMode: setSelectedDisplayMode,
    } = usePersistedListParams({
        keys: {
            filters: 'filters',
            sortBy: 'machineSortBy',
            displayMode: 'selectedMachineDisplayMode',
        },
        defaults: {
            filters: {
                debouncedSearch: '',
                selectedRegion: '',
                selectedSite: '',
                selectedPole: '',
                selectedMetier: '',
                selectedType: '',
                selectedStatus: '',
                selectedMark: '',
                internal_codification: '',
                model: '',
                dimensions: '',
                control_date: '',
                next_control_date: '',
                commissioning_date: '',
                contract_start_date: '',
                contract_end_date: '',
                electric_power: '',
                intensity: '',
                purchase_date: '',
                manufacture_date: '',
                purchase_price: '',
                serial_number: '',
                entreprise: '',
                machineNote: false,
                selectedArchiveStatus: location.pathname.includes('machines_archivees') ? 'Archived' : 'Unarchived',
            },
            sortBy: defaultSortBy,
            displayMode: 'grid',
        },
    });

    const [machines, setMachines] = useState([])
    const [regions, setRegions] = useState([]);
    const [sites, setSites] = useState([]);
    const [poles, setPoles] = useState([]);
    const [metiers, setMetiers] = useState([]);
    const [brands, setBrands] = useState([]);
    const [types, setTypes] = useState([]);
    const [status, setStatus] = useState([]);
    const [machineNoteCount, setMachineNoteCount] = useState(null);
    const [totalMachines, setTotalMachines] = useState('');
    const [filteredMachinesCount, setFilteredMachinesCount] = useState('');
    const [totalPoleMachines, setTotalPoleMachines] = useState('');
    const [totalPoleMetiers, setTotalPoleMetiers] = useState('');
    const [machinesFetched, setMachinesFetched] = useState(0);
    const [machineUndefinedData, setMachineUndefinedData] = useState();
    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'machines',
        pathname: location.pathname,
        displayMode: selectedDisplayMode,
        itemSizes: { grid: 385, list: 70 },
        resetOnFilters: filters,
        resetOnSort: sortBy,
        ready: !loading,
    });
    const limit = 30;

    const filtersEmpty = isObjectEmpty(filters, ['selectedArchiveStatus']);

    useEffect(() => {
        setLoading(true)
    }, [filters, sortBy]);

    useEffect(() => {
        const nextStatus = location.pathname.includes('machines_archivees') ? 'Archived' : 'Unarchived';
        setFilters((prevFilters) => (
            prevFilters.selectedArchiveStatus === nextStatus
                ? prevFilters
                : { ...prevFilters, selectedArchiveStatus: nextStatus }
        ));
    }, [location.pathname, setFilters]);

    const fetchMachines = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('machine', { params: { offset: adjustedOffset, limit, filters, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedMachines = [];
                // Update the fetched records in their correct positions
                response.data.machines.forEach((file, index) => {
                    updatedMachines[adjustedOffset + index] = file;
                });
                // Update the state with the new files array
                setLoading(false);
                setMachines(updatedMachines);
                setMachineNoteCount(response.data.activeNoteMachinesCount);
                setFilteredMachinesCount(response.data.filteredMachinesCount);
                setTotalMachines(response.data.totalMachinesCount)
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching machines:', error);
            CustomErrorLogger(error, 'fetch machines in machines page');
        }
    }, [offset, filters, sortBy]);


    const fetchMachinesFiltrationArrays = useCallback(async () => {
        try {
            const response = await axios.get('machine/getMachineFiltrationArrays', { params: { filters } });
            if (response.status === 200 || response.status === 201) {
                setRegions(response.data.regionArray);
                setSites(response.data.siteArray);
                setPoles(response.data.poleArray);
                setMetiers([...response.data.metierArray]);
                setBrands([...response.data.brandArray]);
                setTypes([...response.data.typeArray]);
                setStatus(response.data.statusArray);
                setTotalPoleMachines(response.data.totalPoleMachineCount);
                setTotalPoleMetiers(response.data.totalMetierCount);
                setMachineUndefinedData(response.data.hasUndefinedValues);
            }
        } catch (error) {
            console.error('Error Fetching machines filtration arrays:', error);
            CustomErrorLogger(error, 'fetch machines filtration arrays in machines page');
        }
    }, [filters]);

    useEffect(() => {
        fetchMachines();
    }, [fetchMachines]);

    useEffect(() => {
        if (location.state) {
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.pathname, location.state, navigate]);

    useEffect(() => {
        fetchMachinesFiltrationArrays()
    }, [fetchMachinesFiltrationArrays]);

    return (
        <ListNavigationProvider value={navigationValue}>
        <Page title="Machines" list={true}>
            <MachinePageHeader setOpenAddMachine={setOpenAddMachine} />
            <Divider />
            {loading ?
                <Box>
                    <MachineFiltration
                        filtersEmpty={filtersEmpty}
                        regions={regions}
                        sites={sites}
                        poles={poles}
                        metiers={metiers}
                        brands={brands}
                        types={types}
                        status={status}
                        machineNoteCount={machineNoteCount}
                        totalMachines={totalMachines}
                        totalPoleMachines={totalPoleMachines}
                        totalPoleMetiers={totalPoleMetiers}
                        filters={filters}
                        setFilters={setFilters}
                        selectedDisplayMode={selectedDisplayMode}
                        setSelectedDisplayMode={setSelectedDisplayMode}
                        filteredMachinesCount={filteredMachinesCount}
                        machinesFetched={machinesFetched}
                    />
                    <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
                </Box> :
                totalMachines === 0 ?
                    <NoRecord
                        description="Aucun machine ajouté pour le moment"
                        buttonTitle="Ajouter une machine"
                        handleOpenDialog={() => setOpenAddMachine(true)}
                        hasPermission={hasMachineCreatePermission}
                    /> :
                    <Box>
                        <MachineFiltration
                            filtersEmpty={filtersEmpty}
                            regions={regions}
                            sites={sites}
                            poles={poles}
                            metiers={metiers}
                            brands={brands}
                            types={types}
                            status={status}
                            machineNoteCount={machineNoteCount}
                            totalMachines={totalMachines}
                            totalPoleMachines={totalPoleMachines}
                            totalPoleMetiers={totalPoleMetiers}
                            filters={filters}
                            setFilters={setFilters}
                            selectedDisplayMode={selectedDisplayMode}
                            setSelectedDisplayMode={setSelectedDisplayMode}
                            filteredMachinesCount={filteredMachinesCount}
                            machinesFetched={machinesFetched}
                        />

                        {selectedDisplayMode === 'grid' ? (
                            <GridView
                                scrollRef={scrollRef}
                                machines={machines}
                                setOffset={setOffset}
                                filteredMachinesCount={filteredMachinesCount}
                                setMachinesFetched={setMachinesFetched}
                            />
                        ) : (
                            <ListView
                                scrollRef={scrollRef}
                                machines={machines}
                                regions={regions}
                                sites={sites}
                                poles={poles}
                                metiers={metiers}
                                brands={brands}
                                types={types}
                                status={status}
                                filters={filters}
                                setFilters={setFilters}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                setOffset={setOffset}
                                filteredMachinesCount={filteredMachinesCount}
                                setMachinesFetched={setMachinesFetched}
                                machineUndefinedData={machineUndefinedData}
                            />
                        )}
                    </Box>
            }
            {openAddMachine &&
                <AddMachineDialoge
                    machines={machines}
                    setMachines={setMachines}
                    totalMachines={totalMachines}
                    setTotalMachines={setTotalMachines}
                    filteredMachinesCount={filteredMachinesCount}
                    setFilteredMachinesCount={setFilteredMachinesCount}
                    openAddMachine={openAddMachine}
                    setOpenAddMachine={setOpenAddMachine}
                    setOpenSuccessfulAddMachine={setOpenSuccessfulAddMachine}
                    setMachineId={setMachineId}
                />}
            {openSuccessfulAddMachine &&
                <SuccessfulAddMachineDialog
                    openSuccessfulAddMachine={openSuccessfulAddMachine}
                    setOpenSuccessfulAddMachine={setOpenSuccessfulAddMachine}
                    machineId={machineId}
                    setOpenAddMachine={setOpenAddMachine}
                />}
        </Page>
        </ListNavigationProvider>
    );
};

export default Machines;